import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Buildings, Handshake, MapTrifold, ChartLineUp, Key, ArrowRight } from "@phosphor-icons/react";

// Kept in sync with the "Five ways we work" panel on the Home page.
const items = [
  {
    icon: Buildings,
    title: "Sales & Advisory",
    copy: "Discreet, off-market and on-market residential mandates across Dubai's premium communities.",
    detail: "We represent sellers and buyers with the same discipline — private negotiations, curated buyer pools, and full transaction management from LOI to DLD.",
    to: "/properties",
    cta: "Browse mandates",
  },
  {
    icon: Handshake,
    title: "Leasing",
    copy: "Residential and commercial leasing with tenant vetting, Ejari and full lifecycle management.",
    detail: "Our leasing desk handles pricing strategy, viewings, tenant background checks, and renewals — covering both furnished serviced and long-lease apartments and villas.",
    to: "/rent",
    cta: "Rental inventory",
  },
  {
    icon: MapTrifold,
    title: "Off-plan",
    copy: "Curated developer launches with priority allocation and payment-plan modelling.",
    detail: "We work directly with Emaar, Damac, Sobha, Nakheel and 80+ UAE developers to secure priority units, structure payment plans and coordinate handover.",
    to: "/projects",
    cta: "Explore launches",
  },
  {
    icon: ChartLineUp,
    title: "Investment desk",
    copy: "Yield modelling, portfolio construction, Golden Visa strategy for individuals and family offices.",
    detail: "From single-unit yield studies to multi-property portfolios, we produce investment memos, cash-flow forecasts and Golden Visa eligibility structures.",
    to: "/calculators",
    cta: "Use the calculators",
  },
  {
    icon: Key,
    title: "Property management",
    copy: "Hand-off management for owners abroad — tenants, snagging, service charges, and reporting.",
    detail: "A single point of contact for landlords who prefer to be hands-off. Monthly reports, on-site inspections, maintenance coordination and full income accounting.",
    to: "/contact",
    cta: "Speak to us",
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <SEO
        title="Services — sales, leasing, off-plan, investment, management"
        description="Point Penta's five service lines: sales & advisory, leasing, off-plan, investment desk and property management."
        path="/services"
      />
      <section className="pt-6 pb-14 border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]} />
          <div className="overline text-[#001588] mb-4">Services</div>
          <h1 className="font-serif text-5xl lg:text-7xl leading-tight max-w-4xl">
            Five ways we work.<br/>
            <span className="italic text-neutral-600">Executed with precision.</span>
          </h1>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map(({ icon: Icon, title, copy, detail, to, cta }, i) => (
            <Link
              key={title}
              to={to}
              data-testid={`service-tile-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group relative flex flex-col border border-neutral-200 bg-white p-8 hover:border-[#001588] transition-all duration-500 hover:-translate-y-1"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 flex-shrink-0 border border-[#001588] flex items-center justify-center group-hover:bg-[#001588] transition-colors">
                  <Icon size={22} className="text-[#001588] group-hover:text-white transition-colors" weight="thin" />
                </div>
                <div className="overline text-neutral-400">0{i + 1}</div>
              </div>
              <h2 className="font-serif text-2xl mb-3 text-[#050d19] group-hover:text-[#001588] transition-colors">{title}</h2>
              <p className="text-neutral-600 leading-relaxed text-sm mb-4">{copy}</p>
              <p className="text-neutral-500 leading-relaxed text-sm mb-6 flex-1">{detail}</p>
              <div className="pt-4 border-t border-neutral-200 flex items-center justify-between text-xs uppercase tracking-[0.2em] text-[#001588]">
                {cta}
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="py-24 border-t border-neutral-100 bg-[#ffffff]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-10 items-center">
          <h2 className="font-serif text-4xl lg:text-5xl leading-tight">
            Something more specific?<br/>
            <span className="italic text-neutral-600">We probably do that too.</span>
          </h2>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact" className="px-6 py-4 bg-[#001588] text-white text-center tracking-wide hover:bg-black transition-colors" data-testid="services-cta-contact">Speak to a partner</Link>
            <Link to="/valuation" className="px-6 py-4 border border-neutral-400 text-center tracking-wide hover:border-[#001588] hover:text-[#001588] transition-colors" data-testid="services-cta-valuation">Free valuation</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
